import { formatDate, formatRelative } from '../../lib/format'
import type { ImproveOverview } from '../../types/improve'

// The loop at a glance: what is waiting, what it could not do by itself, what it learned, how many rules are
// live, and when it next runs. The needs-you number is the one that must never read as a success — it is the
// count of corrections the loop could not generalise, so it carries the warning tone whenever it is not zero.

export function Glance({ view, onPick, pane }: {
  view: ImproveOverview
  pane: string
  onPick: (pane: 'ledger' | 'runs' | 'rules', status?: 'open' | 'needs-human' | 'improved') => void
}) {
  const last = view.lastRun ?? null
  const next = view.nextDueUtc ? new Date(view.nextDueUtc) : null
  const total = view.open + view.needsHuman + view.improved

  return (
    <section className="glance improve-glance" aria-label="The improvement loop at a glance">
      <div className="glance-words">
        <p className="kicker">What happened last night</p>
        {last ? (
          <>
            <p className="glance-summary">{last.outcome}</p>
            <dl className="glance-conventions">
              <dt>Read</dt>
              <dd><span className="chip"><em>{last.gathered}</em> corrections</span></dd>
              <dt>Learned</dt>
              <dd>
                <span className={`chip ${last.applied > 0 ? '' : 'off'}`}><em>{last.applied}</em> rules applied</span>
                {last.needsHuman > 0 && <span className="chip warn"><em>{last.needsHuman}</em> handed back to you</span>}
                {last.declined > 0 && <span className="chip off"><em>{last.declined}</em> declined</span>}
              </dd>
              <dt>Ran</dt>
              <dd><span className="chip">{formatRelative(last.ranAt)}{last.trigger === 'manual' ? ' · by hand' : ''}</span></dd>
            </dl>
          </>
        ) : (
          <p className="glance-summary muted">
            The loop has not run yet. Correct anything Scout gets wrong and the first run reads those corrections,
            works out the rule that would have caught each one, and proves it against everything already on file.
          </p>
        )}
      </div>

      <div className="glance-stats">
        <button className={`glance-stat pressable ${pane === 'ledger' ? 'on' : ''}`} onClick={() => onPick('ledger', 'open')}>
          <strong>{view.open}</strong>
          <span>waiting for tonight</span>
        </button>
        <button className={`glance-stat pressable ${view.needsHuman > 0 ? 'warn' : ''}`} onClick={() => onPick('ledger', 'needs-human')}>
          <strong>{view.needsHuman}</strong>
          <span>need you</span>
        </button>
        <button className="glance-stat pressable" onClick={() => onPick('ledger', 'improved')}>
          <strong>{view.improved}</strong>
          <span>improved</span>
        </button>
        <button className={`glance-stat pressable ${pane === 'rules' ? 'on' : ''}`} onClick={() => onPick('rules')}>
          <strong>{view.activeRules}</strong>
          <span>rules live</span>
        </button>
        <div className="glance-stat">
          <strong>{next ? next.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}</strong>
          <span>{next ? `next run · ${formatDate(view.nextDueUtc!)}` : 'no run scheduled'}</span>
        </div>
        <div className="glance-busiest">
          <p className="kicker">Everything you have corrected</p>
          <p className="muted small">
            {total === 0
              ? 'Nothing yet. The "Not right?" affordance sits on every claim Scout makes.'
              : `${total} correction${total === 1 ? '' : 's'} on the ledger; ${view.improved} of them changed how Scout reads your mail.`}
          </p>
        </div>
      </div>
    </section>
  )
}
